import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '#root/lib/utils';
import styles from './badge.module.scss';

/**
 * Variantes de badge usando class-variance-authority + SCSS modules
 */
const badgeVariants = cva(styles.badge, {
  variants: {
    variant: {
      default: styles.default,
      primary: styles.primary,
      secondary: styles.secondary,
      success: styles.success,
      warning: styles.warning,
      error: styles.error,
      info: styles.info,
      outline: styles.outline,
      primarySolid: styles.primarySolid,
      shipping: styles.shipping,
      match: styles.match,
      one: styles.one,
    },
    size: {
      sm: styles.sm,
      md: styles.md,
      lg: styles.lg,
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Icono a mostrar a la izquierda del label
   * @example <Badge icon={<CheckIcon />}>Activo</Badge>
   */
  icon?: React.ReactNode;
  /**
   * Mostrar un dot indicator a la izquierda
   * @example <Badge dot>Nuevo</Badge>
   */
  dot?: boolean;
  /**
   * Si true, permite cerrar el badge con una X
   * @example <Badge dismissible onDismiss={() => console.log('dismissed')}>Removible</Badge>
   */
  dismissible?: boolean;
  /**
   * Callback cuando se cierra el badge (solo si dismissible=true)
   */
  onDismiss?: () => void;
  /**
   * Click handler — convierte el badge en clickeable con cursor pointer
   * @example <Badge variant="match" onClick={() => openDialog()}>Match</Badge>
   */
  onClick?: () => void;
}

/**
 * Badge Component - Design System
 *
 * Componente de badge/pill/tag moderno con SCSS modules.
 * Soporta múltiples variantes, tamaños, iconos y estados.
 *
 * @example
 * // Badge default
 * <Badge>Default</Badge>
 *
 * @example
 * // Badge con variantes semánticas
 * <Badge variant="success">Exitoso</Badge>
 * <Badge variant="error">Error</Badge>
 * <Badge variant="warning">Advertencia</Badge>
 *
 * @example
 * // Badge con icono
 * <Badge variant="primary" icon={<CheckIcon />}>Verificado</Badge>
 *
 * @example
 * // Badge con dot indicator
 * <Badge variant="success" dot>Online</Badge>
 *
 * @example
 * // Badge removible
 * <Badge dismissible onDismiss={() => alert('Removed')}>
 *   Remover
 * </Badge>
 *
 * @example
 * // Badge outline
 * <Badge variant="outline">Outline</Badge>
 *
 * @example
 * // Badge small
 * <Badge size="sm">Pequeño</Badge>
 */
const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      variant,
      size,
      icon,
      dot,
      dismissible,
      onDismiss,
      onClick,
      children,
      ...props
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = React.useState(true);

    const handleDismiss = () => {
      setIsVisible(false);
      onDismiss?.();
    };

    if (!isVisible) return null;

    // Con onClick es un botón: recibe foco con Tab y se activa con Enter o Espacio
    const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault(); // Espacio no hace scroll
        onClick?.();
      }
    };

    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, size }), onClick && styles.clickable, className)}
        onClick={onClick}
        onKeyDown={onClick ? handleKeyDown : undefined}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        {...props}>
        {/* Dot indicator */}
        {dot && <span className={styles.dot} aria-hidden="true" />}

        {/* Icon */}
        {icon && <span className={styles.icon}>{icon}</span>}

        {/* Children */}
        {children && <span className={styles.label}>{children}</span>}

        {/* Dismiss button */}
        {dismissible && (
          <button
            type="button"
            className={styles.dismissButton}
            onClick={handleDismiss}
            aria-label="Remover">
            <svg
              className={styles.dismissIcon}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        )}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export { Badge, badgeVariants };
